import { useState, useEffect } from 'react';
import { reportService } from '../api/reports';
import StatusBadge from '../components/ui/StatusBadge';
import { Link } from 'react-router-dom';
import { PlusCircle, FileText, Clock, AlertCircle } from 'lucide-react';
import FilePreview from '../components/ui/FilePreview';
import { useTranslation } from 'react-i18next';

export default function TutorReports() {
    const { t } = useTranslation();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedReport, setSelectedReport] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    useEffect(() => {
        loadReports();
    }, []);

    const loadReports = async () => {
        try {
            const data = await reportService.getAll();
            setReports(data);
        } catch (error) {
            console.error("Failed to load reports", error);
        } finally {
            setLoading(false);
        }
    };

    const openPreview = (report) => {
        setSelectedReport(report);
        setPreviewOpen(true);
    };

    if (loading) return (
        <div className="flex justify-center items-center h-64 text-gray-500">
            <Clock className="w-6 h-6 animate-spin mr-2" /> Loading...
        </div>
    );

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center bg-gradient-to-r from-blue-50 to-white p-6 rounded-3xl border border-blue-100/50">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('menu_my_reports')}</h1>
                    <p className="text-sm text-gray-500 mt-1 font-medium">Manage and track your submissions</p>
                </div>
                <Link
                    to="/reports/new"
                    className="bg-primary-600 hover:bg-primary-700 text-white px-5 py-3 rounded-xl flex items-center shadow-lg shadow-primary-500/30 transition-all hover:scale-105 active:scale-95 font-medium"
                >
                    <PlusCircle className="w-5 h-5 mr-2" />
                    {t('menu_new_report')}
                </Link>
            </div>

            <div className="grid gap-6">
                {reports.map((report) => (
                    <div key={report.id} className="bg-white p-6 rounded-2xl shadow-soft border border-gray-100/50 hover:shadow-xl transition-all duration-300 group">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-primary-600 transition-colors">{report.title}</h3>
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{new Date(report.created_at).toLocaleDateString()}</span>
                            </div>
                            <StatusBadge status={report.status} />
                        </div>

                        <p className="text-gray-600 mb-6 bg-surface-50 p-4 rounded-xl text-sm leading-relaxed border border-gray-100">
                            {report.content.length > 200 ? report.content.substring(0, 200) + '...' : report.content}
                        </p>

                        {report.status === 'rejected' && report.head_comment && (
                            <div className="mb-4 bg-red-50 p-4 rounded-xl border border-red-100 flex items-start">
                                <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
                                <div>
                                    <p className="text-xs font-bold text-red-700 uppercase mb-1">Rejection Reason</p>
                                    <p className="text-sm text-red-800">{report.head_comment}</p>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-between items-center pt-2">
                            {report.file ? (
                                <button
                                    onClick={() => openPreview(report)}
                                    className="flex items-center text-gray-500 hover:text-primary-600 font-medium text-sm transition-colors py-2 px-3 rounded-lg hover:bg-surface-50"
                                >
                                    <FileText className="w-4 h-4 mr-2" />
                                    View File
                                </button>
                            ) : (
                                <span className="text-sm text-gray-300 italic px-3">No file</span>
                            )}

                            <Link
                                to={`/reports/${report.id}/edit`}
                                className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors
                    ${report.status === 'draft' || report.status === 'rejected'
                                        ? 'text-primary-600 bg-primary-50 hover:bg-primary-100'
                                        : 'text-gray-300 cursor-not-allowed bg-gray-50'}`}
                                state={{ report }} // Pass state to avoid reload
                                onClick={(e) => {
                                    if (report.status !== 'draft' && report.status !== 'rejected') e.preventDefault();
                                }}
                            >
                                Edit Report
                            </Link>
                        </div>
                    </div>
                ))}

                {reports.length === 0 && (
                    <div className="text-center py-24 bg-white rounded-3xl border border-dashed border-gray-200">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-surface-50 mb-6">
                            <FileText className="w-10 h-10 text-gray-300" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">No Reports Yet</h3>
                        <p className="text-gray-500 mb-8 max-w-sm mx-auto">Start by creating your first report using the button above.</p>
                    </div>
                )}
            </div>

            <FilePreview
                isOpen={previewOpen}
                onClose={() => setPreviewOpen(false)}
                fileUrl={selectedReport?.file}
            />
        </div>
    );
}
